import {
  createApiHeaders,
  tenantAwareApiWrapper,
  withOrchestratorClient,
} from './portal-http';
import { toBulkFilterBody } from '../types';
import type { FindingsBulkFilterPayload } from '../types';
import { reportsClient } from '@terra/shared-rest-clients-reports';
import type {
  ReportsContract,
  ReportsListPageQuerySchema,
} from '@terra/shared-contracts-reports';
import type { z } from 'zod';
import type { ReportFileStatusSchema } from '@terra/shared-schemas-orchestrator';

export const portalReportsApi = {
  getReportsByApplication: (
    applicationId: string,
    query?: z.input<typeof ReportsListPageQuerySchema>,
  ) =>
    tenantAwareApiWrapper<typeof ReportsContract.getReportsByApplicationId>({
      endpointFn: (headers) =>
        reportsClient.getReportsByApplicationId({
          params: { id: applicationId },
          ...(query ? { query } : {}),
          ...withOrchestratorClient(headers),
        }),
    }),

  getReport: (reportId: string) =>
    tenantAwareApiWrapper<typeof ReportsContract.getReport>({
      endpointFn: (headers) =>
        reportsClient.getReport({
          params: { id: reportId },
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteReport: (reportId: string, _applicationId: string) =>
    tenantAwareApiWrapper<typeof ReportsContract.deleteReport>({
      endpointFn: (headers) =>
        reportsClient.deleteReport({
          params: { id: reportId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getReportFiles: (reportId: string) =>
    tenantAwareApiWrapper<typeof ReportsContract.getReportFiles>({
      endpointFn: (headers) =>
        reportsClient.getReportFiles({
          params: { id: reportId },
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteReportFile: (reportId: string, fileId: string) =>
    tenantAwareApiWrapper<typeof ReportsContract.deleteReportFile>({
      endpointFn: (headers) =>
        reportsClient.deleteReportFile({
          params: { id: reportId, fileId },
          ...withOrchestratorClient(headers),
        }),
    }),

  uploadReportFiles: async (reportId: string, files: File[]) =>
    tenantAwareApiWrapper<typeof ReportsContract.uploadReportFiles>({
      endpointFn: (headers) =>
        reportsClient.uploadReportFiles({
          params: { id: reportId },
          body: { files },
          ...withOrchestratorClient(headers),
        }),
    }),

  getReportFilePassword: (reportId: string, fileId: string) =>
    tenantAwareApiWrapper<typeof ReportsContract.getReportFilePassword>({
      endpointFn: (headers) =>
        reportsClient.getReportFilePassword({
          params: { id: reportId, fileId },
          ...withOrchestratorClient(headers),
        }),
    }),

  updateReportFileStatus: (
    reportId: string,
    fileId: string,
    status:
      | typeof ReportFileStatusSchema.enum.DRAFT
      | typeof ReportFileStatusSchema.enum.FINAL,
  ) =>
    tenantAwareApiWrapper<typeof ReportsContract.updateReportFileStatus>({
      endpointFn: (headers) =>
        reportsClient.updateReportFileStatus({
          params: { id: reportId, fileId },
          body: { status },
          ...withOrchestratorClient(headers),
        }),
    }),

  createReport: (findings: string[], applicationId: string, title?: string) =>
    tenantAwareApiWrapper<typeof ReportsContract.createReport>({
      endpointFn: (headers) =>
        reportsClient.createReport({
          body: {
            findings,
            title,
            isNewReport: true,
          },
          ...withOrchestratorClient(headers),
        }),
    }),

  updateReport: ({
    reportId,
    title,
    nextStepsSummary,
    contactName,
    reportDate,
    testPeriodStart,
    testPeriodEnd,
    exclusions,
    limitations,
    methodologyType,
  }: {
    reportId: string;
    title?: string;
    nextStepsSummary?: string | null;
    contactName?: string | null;
    reportDate?: Date | null;
    testPeriodStart?: Date | null;
    testPeriodEnd?: Date | null;
    exclusions?: string[];
    limitations?: string[];
    methodologyType?: 'white-box' | 'gray-box' | 'black-box';
  }) =>
    tenantAwareApiWrapper<typeof ReportsContract.updateReport>({
      endpointFn: (headers) =>
        reportsClient.updateReport({
          params: { id: reportId },
          body: {
            findings: [], // Required but not changing
            title,
            nextStepsSummary,
            contactName,
            reportDate,
            testPeriodStart,
            testPeriodEnd,
            exclusions,
            limitations,
            methodologyType,
          },
          ...withOrchestratorClient(headers),
        }),
    }),

  generateReportWithEnrichment: ({
    findings,
    applicationId,
    title,
    isNewReport,
    reportId,
    jobId,
  }: {
    findings: string[];
    applicationId: string;
    title?: string;
    isNewReport?: boolean;
    reportId?: string;
    jobId?: string;
  }) =>
    tenantAwareApiWrapper<typeof ReportsContract.generateReportWithEnrichment>({
      endpointFn: (headers) =>
        reportsClient.generateReportWithEnrichment({
          params: { applicationId },
          body: {
            findings,
            title,
            isNewReport: isNewReport ?? true,
            reportId,
            jobId,
          },
          ...withOrchestratorClient(headers),
        }),
    }),

  addFindingsToReport: (
    reportId: string,
    findingsIds: string[],
    applicationId: string,
  ) =>
    tenantAwareApiWrapper<typeof ReportsContract.addFindingsToReport>({
      endpointFn: (headers) =>
        reportsClient.addFindingsToReport({
          params: { reportId },
          body: {
            findingsIds,
            applicationId,
          },
          ...withOrchestratorClient(headers),
        }),
    }),

  addFindingsToReportByFilter: async (
    reportId: string,
    payload: FindingsBulkFilterPayload,
  ) => {
    try {
      const headers = await createApiHeaders();
      const response = await reportsClient.addFindingsToReportByFilter({
        params: { reportId },
        body: toBulkFilterBody({ payload, includeUnpublished: false }),
        ...withOrchestratorClient(headers),
      });

      return response;
    } catch (error) {
      console.error('Failed to add findings to report by filter:', error);

      throw error;
    }
  },

  // Scoping endpoints - using orchestrator service
};
